import { addDays, isBefore, parseISO, startOfDay } from "date-fns";
import type { TFunction } from "i18next";
import { generatePath } from "react-router-dom";

import { NOTIFICATION_ACTIONS, type NotificationAction } from "./constants";
import { EVENT_TYPES, REMOTE_FAILURE_EVENT_TYPES } from "./event-types";
import { notificationStatusSchema } from "./schemas";
import type {
  GetNotificationsResponse,
  NotificationActionTarget,
  NotificationFilterOptionsParams,
  NotificationData,
  NotificationFilters,
  NotificationLinks,
  NotificationPrimaryAction,
  NotificationSelectionMode,
  NotificationRouteAccess,
  NotificationSeverity,
  NotificationStatus,
  NotificationsInfiniteData,
  RemoteFailureNotification,
} from "./types";

import { ROUTES } from "@/constants/routes";
import type { Integration } from "@/features/integrations/types";
import { ModuleName } from "@/features/integrations/utils/module-name";
import { encodeSlug } from "@/features/integrations/utils/slug";
import type { EntityName } from "@/lib/hooks/usePermissionMatrix";
import { parseLocalDateOnly } from "@/utils/dateUtils";

export const LIVE_MATCH = {
  MATCH: "match",
  MISS: "miss",
  UNKNOWN: "unknown",
} as const;

type LiveMatch = (typeof LIVE_MATCH)[keyof typeof LIVE_MATCH];

export function toLocalDayWindow({ dateFrom, dateTo }: Pick<NotificationFilterOptionsParams, "dateFrom" | "dateTo">) {
  const fromDay = parseLocalDateOnly(dateFrom);
  const toDay = parseLocalDateOnly(dateTo);
  return {
    from: fromDay && startOfDay(fromDay),
    toExclusive: toDay && startOfDay(addDays(toDay, 1)),
  };
}

export function flattenNotificationPages(pages: GetNotificationsResponse[] = []): NotificationData[] {
  const byId = new Map<string, NotificationData>();
  for (const page of pages) {
    for (const notification of page.data) {
      if (!byId.has(notification.id)) byId.set(notification.id, notification);
    }
  }
  return [...byId.values()];
}

export function prependNotification(
  prev: NotificationsInfiniteData | undefined,
  notification: NotificationData,
): NotificationsInfiniteData | undefined {
  const [firstPage, ...restPages] = prev?.pages ?? [];
  if (!prev || !firstPage) return prev;
  if (prev.pages.some((page) => page.data.some((n) => n.id === notification.id))) return prev;

  return {
    ...prev,
    pages: [
      { ...firstPage, data: [notification, ...firstPage.data], totalRecords: firstPage.totalRecords + 1 },
      ...restPages.map((page) => ({ ...page, totalRecords: page.totalRecords + 1 })),
    ],
  };
}

export function matchLiveNotification(filters: NotificationFilters, notification: NotificationData): LiveMatch {
  if (filters.status === notificationStatusSchema.enum.archived) return LIVE_MATCH.MISS;
  const createdAt = parseISO(notification.createdAt);
  const { from, toExclusive } = toLocalDayWindow(filters);
  if (from && isBefore(createdAt, from)) return LIVE_MATCH.MISS;
  if (toExclusive && !isBefore(createdAt, toExclusive)) return LIVE_MATCH.MISS;
  if (filters.severities.length > 0 && !filters.severities.includes(notification.severity)) return LIVE_MATCH.MISS;
  if (
    filters.clientIds.length > 0 &&
    (!notification.clientId || !filters.clientIds.includes(notification.clientId))
  ) return LIVE_MATCH.MISS;
  if (filters.search || filters.actorIds.length > 0) return LIVE_MATCH.UNKNOWN;
  return LIVE_MATCH.MATCH;
}

export function parseNotificationStatus(value: string | null): NotificationStatus {
  return notificationStatusSchema.catch(notificationStatusSchema.enum.unread).parse(value);
}

const TIMESTAMP_FRACTION_DIGITS = 7;

function toComparableTimestamp(timestamp: NotificationData["createdAt"]) {
  const [seconds = "", fraction = ""] = timestamp.replace(/Z$/, "").split(".");
  return `${seconds}.${fraction.padEnd(TIMESTAMP_FRACTION_DIGITS, "0")}`;
}

export function isCreatedAtOrBefore(createdAt: NotificationData["createdAt"], bound: NotificationData["createdAt"]) {
  return toComparableTimestamp(createdAt) <= toComparableTimestamp(bound);
}

function toTargetMatcher(target: NotificationActionTarget) {
  if ("notificationIds" in target) {
    const notificationIds = new Set(target.notificationIds);
    return (notification: NotificationData) => notificationIds.has(notification.id);
  }
  const excludedIds = new Set(target.excludedIds);
  return (notification: NotificationData) =>
    !excludedIds.has(notification.id) && isCreatedAtOrBefore(notification.createdAt, target.createdAtOrBefore);
}

export function doesActionLeaveView(viewStatus: NotificationStatus, action: NotificationAction, keepInView: boolean) {
  if (keepInView) return false;
  switch (action) {
  case NOTIFICATION_ACTIONS.READ:
    return viewStatus === notificationStatusSchema.enum.unread;
  case NOTIFICATION_ACTIONS.ARCHIVE:
    return viewStatus !== notificationStatusSchema.enum.archived;
  case NOTIFICATION_ACTIONS.UNARCHIVE:
    return viewStatus === notificationStatusSchema.enum.archived;
  default:
    return false;
  }
}

export function getReadToggleAction(notification: NotificationData): NotificationAction {
  return notification.readAt === null ? NOTIFICATION_ACTIONS.READ : NOTIFICATION_ACTIONS.UNREAD;
}

export function getArchiveToggleAction(notification: NotificationData): NotificationAction {
  return notification.archivedAt === null ? NOTIFICATION_ACTIONS.ARCHIVE : NOTIFICATION_ACTIONS.UNARCHIVE;
}

export function getBulkArchiveAction(viewStatus: NotificationStatus): NotificationAction {
  return viewStatus === notificationStatusSchema.enum.archived
    ? NOTIFICATION_ACTIONS.UNARCHIVE
    : NOTIFICATION_ACTIONS.ARCHIVE;
}

type ActedAt = NonNullable<NotificationData["readAt"]>;

function getReadAtAfter(notification: NotificationData, action: NotificationAction, actedAt: ActedAt) {
  if (action === NOTIFICATION_ACTIONS.READ) return notification.readAt ?? actedAt;
  if (action === NOTIFICATION_ACTIONS.UNREAD) return null;
  return notification.readAt;
}

function getArchivedAtAfter(notification: NotificationData, action: NotificationAction, actedAt: ActedAt) {
  if (action === NOTIFICATION_ACTIONS.ARCHIVE) return notification.archivedAt ?? actedAt;
  if (action === NOTIFICATION_ACTIONS.UNARCHIVE) return null;
  return notification.archivedAt;
}

export function applyActionToNotification(
  notification: NotificationData,
  action: NotificationAction,
  actedAt: ActedAt,
): NotificationData {
  return {
    ...notification,
    readAt: getReadAtAfter(notification, action, actedAt),
    archivedAt: getArchivedAtAfter(notification, action, actedAt),
  };
}

export function applyNotificationAction(
  data: NotificationsInfiniteData | undefined,
  viewStatus: NotificationStatus,
  action: NotificationAction,
  target: NotificationActionTarget,
  actedAt: ActedAt,
  keepInView = false,
): NotificationsInfiniteData | undefined {
  if (!data) return data;

  const isActionTargeted = toTargetMatcher(target);

  if (doesActionLeaveView(viewStatus, action, keepInView)) {
    const pages = data.pages.map((page) => ({
      ...page,
      data: page.data.filter((notification) => !isActionTargeted(notification)),
    }));
    const removed = flattenNotificationPages(data.pages).length - flattenNotificationPages(pages).length;
    return {
      ...data,
      pages: pages.map((page) => ({ ...page, totalRecords: page.totalRecords - removed })),
    };
  }

  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      data: page.data.map((notification) =>
        isActionTargeted(notification) ? applyActionToNotification(notification, action, actedAt) : notification,
      ),
    })),
  };
}

function isCountedUnread(notification: NotificationData) {
  return notification.readAt === null && notification.archivedAt === null;
}

export function getUnreadCountDelta(
  cachedNotifications: NotificationData[],
  action: NotificationAction,
  notificationIds: NotificationData["id"][],
  actedAt: ActedAt,
) {
  const targetIds = new Set(notificationIds);
  const targets = new Map<NotificationData["id"], NotificationData>();
  for (const notification of cachedNotifications) {
    if (!targetIds.has(notification.id) || targets.has(notification.id)) continue;
    targets.set(notification.id, notification);
  }
  let delta = 0;
  for (const notification of targets.values()) {
    const after = applyActionToNotification(notification, action, actedAt);
    delta += Number(isCountedUnread(after)) - Number(isCountedUnread(notification));
  }
  return delta;
}

export function getBulkReadAction({
  viewStatus,
  mode,
  hasUnreadSelected,
  matchingUnreadCount,
}: {
  viewStatus: NotificationStatus;
  mode: NotificationSelectionMode;
  hasUnreadSelected: boolean;
  matchingUnreadCount: number | undefined;
}): NotificationAction | undefined {
  if (viewStatus === notificationStatusSchema.enum.archived) return undefined;
  if (mode === "ids") return hasUnreadSelected ? NOTIFICATION_ACTIONS.READ : NOTIFICATION_ACTIONS.UNREAD;
  if (viewStatus === notificationStatusSchema.enum.unread) return NOTIFICATION_ACTIONS.READ;
  if (matchingUnreadCount === undefined) return undefined;
  return matchingUnreadCount > 0 ? NOTIFICATION_ACTIONS.READ : NOTIFICATION_ACTIONS.UNREAD;
}

const CLIENT_SCOPE_PATTERN = `/${ROUTES.clients}/:clientId`;

const LINK_PATTERNS = {
  client: `/${ROUTES.home}`,
  campaign: `/${ROUTES.campaigns}/:campaignId`,
  campaignIntegration: `/${ROUTES.campaigns}/:campaignId/${ROUTES.campaignIntegrations}/:clientModuleIntegrationId`,
  integration: `/${ROUTES.integrations}/:integrationName`,
  integrationCampaigns: `/${ROUTES.integrations}/:integrationName/${ModuleName.Campaigns}`,
} as const;

type LinkTarget = keyof typeof LINK_PATTERNS;

const REVIEW_SYNC_ERROR_ACTION: NotificationPrimaryAction = {
  target: "integrationCampaigns",
  labelKey: "notifications.detail.actions.reviewSyncError",
};

const PRIMARY_ACTIONS = new Map<string, NotificationPrimaryAction>([
  [
    EVENT_TYPES.campaignsUpserted,
    { target: "campaignIntegration", labelKey: "notifications.detail.actions.viewCampaignIntegration" },
  ],
  ...REMOTE_FAILURE_EVENT_TYPES.map((eventType) => [eventType, REVIEW_SYNC_ERROR_ACTION] as const),
  [
    EVENT_TYPES.integrationsCompanyRegistered,
    { target: "integration", labelKey: "notifications.detail.actions.openIntegration" },
  ],
]);

const LINK_ENTITIES: Record<LinkTarget, EntityName> = {
  client: "clients",
  campaign: "campaigns",
  campaignIntegration: "jobBoards",
  integration: "integrations",
  integrationCampaigns: "integrations",
};

function toScopedPath(
  context: NotificationRouteAccess["context"],
  clientId: NotificationData["clientId"],
  pattern: string,
  params: Record<string, string>,
) {
  if (context === "client") return generatePath(pattern, params);
  if (!clientId) return undefined;
  return generatePath(`${CLIENT_SCOPE_PATTERN}${pattern}`, { ...params, clientId });
}

function resolveIntegrationName(integrationName: string, integrationNames: readonly Integration["name"][]) {
  const lowerCaseName = integrationName.toLowerCase();
  return integrationNames.find((name) => name.toLowerCase() === lowerCaseName);
}

function buildTargetPaths(
  notification: NotificationData,
  { context, canView }: NotificationRouteAccess,
  integrationNames: readonly Integration["name"][],
): Partial<Record<LinkTarget, string>> {
  const { clientId, change } = notification;
  if (!change) return {};
  const linkedCampaignId = "campaignId" in change ? change.campaignId : null;
  const linkedIntegrationName = resolveIntegrationName(change.integrationName, integrationNames);

  const toViewablePath = (target: LinkTarget, params: Record<string, string>) =>
    canView(LINK_ENTITIES[target]) ? toScopedPath(context, clientId, LINK_PATTERNS[target], params) : undefined;

  return {
    client: toViewablePath("client", {}),
    campaign: linkedCampaignId ? toViewablePath("campaign", { campaignId: linkedCampaignId }) : undefined,
    campaignIntegration:
      linkedCampaignId && "clientModuleIntegrationId" in change
        ? toViewablePath("campaignIntegration", {
          campaignId: linkedCampaignId,
          clientModuleIntegrationId: change.clientModuleIntegrationId,
        })
        : undefined,
    integration: linkedIntegrationName
      ? toViewablePath("integration", { integrationName: encodeSlug(linkedIntegrationName) })
      : undefined,
    integrationCampaigns: linkedIntegrationName
      ? toViewablePath("integrationCampaigns", { integrationName: encodeSlug(linkedIntegrationName) })
      : undefined,
  };
}

export function getNotificationLinks(
  notification: NotificationData,
  access: NotificationRouteAccess,
  integrationNames: readonly Integration["name"][],
): NotificationLinks {
  const paths = buildTargetPaths(notification, access, integrationNames);
  const primaryAction = PRIMARY_ACTIONS.get(notification.eventType);
  const primaryPath = primaryAction && paths[primaryAction.target];
  return {
    primary: primaryAction && primaryPath ? { to: primaryPath, labelKey: primaryAction.labelKey } : undefined,
    client: paths.client,
    campaign: paths.campaign,
    integration: paths.integration,
  };
}

export function isRemoteFailureNotification(notification: NotificationData): notification is RemoteFailureNotification {
  return (REMOTE_FAILURE_EVENT_TYPES as readonly string[]).includes(notification.eventType) && notification.change !== null;
}

export function getNotificationSeverityStyles(severity: NotificationSeverity) {
  switch (severity) {
  case "Warning":
    return {
      background: "bg-(--badge-warning-bg)",
      text: "text-(--badge-warning-text)",
      icon: "text-warning",
    };
  case "Error":
    return {
      background: "bg-(--badge-error-bg)",
      text: "text-(--badge-error-text)",
      icon: "text-destructive",
    };
  default:
    return {
      background: "bg-(--badge-info-bg)",
      text: "text-(--badge-info-text)",
      icon: "text-info",
    };
  }
}
// The message is built from the change payload's campaign / integration names, so a notification
// that carries no payload has nothing to interpolate and falls back to the generic key.
export function getTranslationKey(notification: NotificationData): string {
  const { eventType, change } = notification;
  if (!change) return "notifications.events.Unknown";
  return `notifications.events.${eventType}`;
}

export function getTranslationValues({ change, clientName }: NotificationData, t: TFunction) {
  return { ...change, clientName: clientName ?? t("notifications.unknownClient") };
}
